import type { MantineColorsTuple } from '@mantine/core';

/**
 * Brand tokens, sampled from the only surviving copy of the mark.
 *
 * There is no vector original and no brand guide, so these hues come from the
 * raster itself — see `docs/assets.md` and the `NAVY` constant in
 * `tools/build_icons.py`, which reads the same pixels. Keeping them here rather
 * than inline means a future redraw updates one file.
 *
 * The scales are ordered light to dark, as Mantine requires, with the sampled
 * hue sitting at index 6 — the shade Mantine treats as "the" colour in light
 * mode.
 */

/** The flat navy of the "F". Matches `NAVY = (31, 45, 71)` in tools/build_icons.py. */
export const BRAND_NAVY = '#1f2d47';

/** The blue in the lower edge of the gradient. The portal's accent. */
export const BRAND_BLUE = '#1a59ea';

export const BRAND_MAGENTA = '#ff006e';
export const BRAND_TEAL = '#00cda6';
export const BRAND_YELLOW = '#ffdf00';

/**
 * The mark's hue rotation, flattened into a line. Used for the sign-in backdrop
 * and nothing else — an admin tool that will grow dense tables cannot carry
 * six saturated hues in its chrome.
 */
export const BRAND_GRADIENT =
  'linear-gradient(135deg, #ff006e 0%, #ff7a1a 22%, #ffdf00 42%, #00cda6 66%, #1a59ea 88%, #6a3df0 100%)';

const brand: MantineColorsTuple = [
  '#eef3ff',
  '#dbe4fc',
  '#b4c7f8',
  '#8aa8f4',
  '#6690f0',
  '#4f7fee',
  BRAND_BLUE,
  '#164ecf',
  '#1044b7',
  '#06399f',
];

/**
 * Overrides Mantine's neutral `dark` scale so dark mode reads as deep navy
 * rather than grey. Index 7 is the body background and is exactly the navy of
 * the "F"; the rest are tinted around it.
 */
const dark: MantineColorsTuple = [
  '#c7cddb',
  '#b0b8cb',
  '#8b95ad',
  '#6b7690',
  '#4d5872',
  '#3a4560',
  '#2b3550',
  BRAND_NAVY,
  '#18243a',
  '#111a2b',
];

const magenta: MantineColorsTuple = [
  '#ffe8f1',
  '#ffd0e0',
  '#ff9ec1',
  '#ff69a0',
  '#ff3d85',
  '#ff2175',
  BRAND_MAGENTA,
  '#e4005f',
  '#cc0054',
  '#b30047',
];

const teal: MantineColorsTuple = [
  '#e0fbf5',
  '#c6f5e9',
  '#92ead4',
  '#5adfbe',
  '#2ed6ac',
  '#12d1a1',
  BRAND_TEAL,
  '#00b490',
  '#009f7f',
  '#00886c',
];

const yellow: MantineColorsTuple = [
  '#fffde5',
  '#fffac9',
  '#fff490',
  '#ffee53',
  '#ffe824',
  '#ffe30a',
  BRAND_YELLOW,
  '#e3c400',
  '#c9ae00',
  '#ad9500',
];

export const brandColors = { brand, dark, magenta, teal, yellow };
